/**
 * Create React App inlines only direct `process.env.REACT_APP_*` references.
 * Avoid `process.env?.REACT_APP_*` — optional chaining can prevent env substitution.
 */

const APP_LOGIN_PATH = '/login';

function trimEnv(value: string | undefined): string | undefined {
  const t = value?.trim();
  return t || undefined;
}

/** Ignore localhost launcher/login URLs in production builds (often leaked from dev .env). */
function configuredExternalUrl(value: string | undefined): string | undefined {
  const trimmed = trimEnv(value);
  if (!trimmed) return undefined;
  if (process.env.NODE_ENV === 'production') {
    try {
      const host = new URL(trimmed).hostname;
      if (host === 'localhost' || host === '127.0.0.1') return undefined;
    } catch {
      return undefined;
    }
  }
  return trimmed;
}

export function isHygoOsLauncherConfigured(): boolean {
  return !!(
    configuredExternalUrl(process.env.REACT_APP_HYGO_OS_LOGIN_URL) ||
    configuredExternalUrl(process.env.REACT_APP_LAUNCHER_URL)
  );
}

/** External Hygo OS login URL when launcher/login env is set; otherwise null (use in-app login). */
export function getHygoOsLoginUrl(): string | null {
  const explicit = configuredExternalUrl(process.env.REACT_APP_HYGO_OS_LOGIN_URL);
  if (explicit) return explicit;
  const launcher = configuredExternalUrl(process.env.REACT_APP_LAUNCHER_URL);
  if (launcher) {
    try {
      return `${new URL(launcher).origin}/login?switchUser=1`;
    } catch {
      /* invalid URL */
    }
  }
  return null;
}

/** External launcher URL when configured; otherwise null. */
export function getLauncherUrl(): string | null {
  const explicit = configuredExternalUrl(process.env.REACT_APP_LAUNCHER_URL);
  if (explicit) return explicit;
  const login = configuredExternalUrl(process.env.REACT_APP_HYGO_OS_LOGIN_URL);
  if (login) {
    try {
      return `${new URL(login).origin}/launcher`;
    } catch {
      /* invalid URL */
    }
  }
  return null;
}

export function getAppLoginPath(): string {
  return APP_LOGIN_PATH;
}

/** After logout or forced session end: Hygo OS login when configured, else in-app /login. */
export function redirectToLoginAfterLogout(): void {
  if (typeof window === 'undefined') return;
  const external = getHygoOsLoginUrl();
  if (external) {
    window.location.href = external;
    return;
  }
  window.location.assign(`${window.location.origin}${APP_LOGIN_PATH}`);
}
